import type {Identified, Ordered} from 'src/typings';
import {ascendingOrderComparator} from 'shared/utils/array-utils';
import {exists} from 'shared/utils/custom-utils';

export interface FlattenedHierarchyNode<ID> extends Identified<ID>, Ordered {
    parentId?: ID;
}

export type SubtreeAppender<T extends FlattenedHierarchyNode<ID>, ID, E extends T> = (node: T, subtrees: E[]) => E;


export function transformIntoHierarchyNodes<T extends FlattenedHierarchyNode<ID>, ID, E extends T>(nodes: T[], subtreeAppender: SubtreeAppender<T, ID, E>, rootNodes: T[] = []): E[] {
    const descendantNodes: T[] = [];
    const filteredNodes = nodes.filter((node) => !rootNodes.includes(node));

    // optimization: for-loop is faster than functional array functions
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < filteredNodes.length; i++) {
        const collection = filteredNodes[i];
        if (exists(collection.parentId)) {
            descendantNodes.push(collection);
        } else {
            rootNodes.push(collection);
        }
    }

    rootNodes = rootNodes.sort(ascendingOrderComparator);
    return rootNodes.flatMap((rootNode) => transformIntoHierarchyNode(rootNode, descendantNodes, subtreeAppender))
}

function transformIntoHierarchyNode<T extends FlattenedHierarchyNode<ID>, ID, E extends T>(currentNode: T, untransformedNodes: T[], subtreeAppender: SubtreeAppender<T, ID, E>): E {
    let directChildNodes: T[] = [];
    const otherDescendantNodes: T[] = [];

    // optimization: for-loop is faster than functional array functions
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < untransformedNodes.length; i++) {
        const untransformedNode = untransformedNodes[i];
        if (untransformedNode.parentId === currentNode.id) {
            directChildNodes.push(untransformedNode);
        } else {
            otherDescendantNodes.push(untransformedNode);
        }
    }

    directChildNodes = directChildNodes.sort(ascendingOrderComparator);
    const subtrees = directChildNodes.flatMap((childNode) => transformIntoHierarchyNode(childNode, otherDescendantNodes, subtreeAppender));
    return subtreeAppender(currentNode, subtrees);
}